import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import AppShell from "@/components/AppShell";
import EnrichmentCard from "@/components/EnrichmentCard";
import AIBriefCard from "@/components/AIBriefCard";
import ProspectStepper from "@/components/ui/ProspectStepper";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GroupedList, GroupedListRow } from "@/components/ui/GroupedList";
import { toast } from "sonner";
import { haptic } from "@/lib/haptics";
import { ArrowLeft, Phone, Mail, MapPin, Trash2, CalendarPlus, Calendar, Footprints, MessageCircle, MoreHorizontal, Building2, Briefcase, Hash, Users, Share2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["À contacter", "Contacté", "En discussion", "RDV planifié", "Converti", "Sans suite"];

/** Convert ISO UTC to local "YYYY-MM-DDTHH:mm" for datetime-local inputs. */
function isoToLocal(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

function localToIso(local) {
  if (!local) return null;
  return new Date(local).toISOString();
}

export default function ProspectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [rdvLocal, setRdvLocal] = useState("");
  const [savingRdv, setSavingRdv] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/prospects/${id}`);
      setData(data);
      setRdvLocal(isoToLocal(data?.prospect?.rdv_at));
    } catch {
      toast.error("Lead introuvable");
      navigate("/pipeline");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const updateStatus = async (status) => {
    try {
      const patch = { status };
      // When marking RDV planifié without an explicit date, default to tomorrow 10:00 local
      if (status === "RDV planifié" && !data?.prospect?.rdv_at) {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        d.setHours(10, 0, 0, 0);
        patch.rdv_at = d.toISOString();
        setRdvLocal(isoToLocal(patch.rdv_at));
      }
      await api.put(`/prospects/${id}`, patch);
      haptic("medium");
      toast.success("Statut mis à jour");
      load();
    } catch { toast.error("Erreur"); }
  };

  const saveRdvAt = async (local) => {
    setSavingRdv(true);
    try {
      const iso = local ? localToIso(local) : null;
      await api.put(`/prospects/${id}`, { rdv_at: iso });
      haptic(iso ? "success" : "light");
      toast.success(iso ? "RDV enregistré" : "RDV effacé");
      load();
    } catch { toast.error("Erreur"); }
    finally { setSavingRdv(false); }
  };

  const addToCalendar = async () => {
    if (!data?.prospect?.rdv_at) {
      toast.error("Sélectionnez d'abord une date de RDV");
      return;
    }
    try {
      const token = localStorage.getItem("prosperr_token");
      const res = await fetch(`${API_BASE}/calendar/prospect/${id}.ics`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rdv-${(data.prospect.name || "lead").replace(/\s+/g, "-")}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Événement téléchargé — ouvrez-le pour l'ajouter à votre calendrier");
    } catch {
      toast.error("Impossible de générer l'événement");
    }
  };

  const del = async () => {
    if (!window.confirm("Supprimer ce lead ?")) return;
    await api.delete(`/prospects/${id}`);
    haptic("warning");
    toast.success("Supprimé");
    navigate("/pipeline");
  };

  const toggleShare = async () => {
    const isShared = !!data?.prospect?.shared_with_team;
    try {
      await api.post(`/prospects/${id}/${isShared ? "unshare" : "share"}`);
      toast.success(isShared ? "Lead retiré du partage" : "Lead partagé avec votre équipe");
      load();
    } catch {
      toast.error("Partage impossible");
    }
  };

  if (!data) return <AppShell><div className="grid place-items-center py-24"><div className="w-8 h-8 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" /></div></AppShell>;
  const p = data.prospect;
  const showRdv = p.status === "RDV planifié" || !!p.rdv_at;
  const initial = (p.name || "?").charAt(0).toUpperCase();
  const rdvFormatted = p.rdv_at ? new Date(p.rdv_at).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <AppShell>
      {/* Floating back button */}
      <button
        onClick={() => navigate(-1)}
        data-testid="back"
        className="fixed top-4 left-5 z-30 w-10 h-10 rounded-full bg-white/90 backdrop-blur-md shadow-soft grid place-items-center active:scale-95 transition-transform"
        aria-label="Retour"
      >
        <ArrowLeft className="w-4 h-4 text-zinc-200" />
      </button>

      {/* Hero — 2026 editorial layout */}
      <div className="relative pt-14 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full" style={{ background: "radial-gradient(closest-side, rgba(0,220,130,0.35), transparent 70%)" }} />
          <div className="absolute -left-16 top-10 w-64 h-64 rounded-full" style={{ background: "radial-gradient(closest-side, rgba(217,70,239,0.18), transparent 70%)" }} />
          <div className="grain-overlay" />
        </div>
        <div className="relative px-5">
          <div className="text-[10px] text-emerald-300 uppercase tracking-[0.3em] mb-3 font-bold">Fiche prospect</div>
          <div className="flex items-end gap-4">
            <div className="w-16 h-16 rounded-2xl bento-tile grid place-items-center font-display text-2xl font-bold text-white shrink-0">
              {initial}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="h-editorial text-[28px] sm:text-[34px] text-white leading-[1.02]" data-testid="prospect-name">
                {p.name || "Sans nom"}
              </h1>
              <div className="text-[12.5px] text-zinc-400 mt-1 truncate">{p.company || "—"}{p.title ? ` · ${p.title}` : ""}</div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <div className="inline-flex items-center gap-1.5 bg-emerald-500/15 border border-emerald-500/30 rounded-md px-2 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-200 tnum">
              Score {p.score}/100
            </div>
            {p.status && (
              <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-300 bg-[var(--surface-alt)] border border-[var(--border-soft)] rounded-md px-2 py-1">
                {p.status}
              </div>
            )}
          </div>
        </div>
        <div className="hairline-gradient mt-6" />
      </div>

      {/* Floating action bar */}
      <div className="px-5 -mt-3">
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-3 flex justify-around">
          {[
            { icon: Footprints, label: "Visite", onClick: () => navigate(`/visit-report/${id}`), testId: "go-visit", color: "violet" },
            { icon: Phone, label: "Appel", onClick: () => p.phone ? (window.location.href = `tel:${p.phone}`) : navigate(`/phone/${id}`), testId: "go-phone", color: "emerald" },
            { icon: Mail, label: "Email", onClick: () => p.email ? (window.location.href = `mailto:${p.email}`) : toast.error("Aucun email"), testId: "go-email", color: "sky" },
            { icon: MessageCircle, label: "Message", onClick: () => navigate(`/social/${id}`), testId: "go-social", color: "fuchsia" },
          ].map(({ icon: I, label, onClick, testId, color }) => {
            const colorMap = {
              violet: "bg-emerald-500/15 text-emerald-400",
              fuchsia: "bg-orange-500/15 text-orange-300",
              emerald: "bg-emerald-500/15 text-emerald-300",
              sky: "bg-sky-500/15 text-sky-300",
            };
            return (
              <button key={testId} onClick={onClick} data-testid={testId} className="flex flex-col items-center gap-1.5 flex-1 active:scale-95 transition-transform">
                <div className={`w-11 h-11 rounded-2xl grid place-items-center ${colorMap[color]}`}>
                  <I className="w-4.5 h-4.5" strokeWidth={2.2} />
                </div>
                <span className="text-[11px] font-semibold text-zinc-400">{label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* AI Brief — synthèse + action recommandée */}
      <div className="px-5 mt-4">
        <AIBriefCard prospectId={id} prospect={p} />
      </div>

      {/* Status stepper (visual) + selector */}
      <div className="px-5 mt-5">
        <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-3 px-1">Cycle de vie</div>
        <div className="bg-[#13131a]/60 backdrop-blur-xl border border-white/10 rounded-3xl p-4">
          <ProspectStepper status={p.status} onSelect={updateStatus} />
          <div className="mt-4 flex gap-2 overflow-x-auto kanban-scroll pb-1" data-testid="status-pills">
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => updateStatus(s)}
                data-testid={`status-pill-${s}`}
                className={`shrink-0 rounded-full px-3 h-8 text-[11px] font-semibold active:scale-95 transition-all ${
                  p.status === s
                    ? "bg-gradient-to-r from-emerald-500 to-orange-500 text-white shadow-[0_0_12px_rgba(0,220,130,0.4)]"
                    : "bg-white/5 border border-white/10 text-slate-300"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Share with team toggle */}
      <div className="px-5 mt-4">
        <button
          onClick={toggleShare}
          data-testid="toggle-share"
          className={`w-full rounded-xl p-4 flex items-center gap-3 active:scale-[0.98] transition-transform border ${
            p.shared_with_team
              ? "bg-emerald-500/15 text-emerald-200 border-emerald-500/40"
              : "bg-[var(--surface)] text-[var(--text)] border-[var(--border)] hover:border-zinc-700"
          }`}
        >
          <div className={`w-10 h-10 rounded-lg grid place-items-center ${p.shared_with_team ? "bg-emerald-500/20 border border-emerald-500/40" : "bg-emerald-500/15 text-emerald-300"}`}>
            {p.shared_with_team ? <Users className="w-5 h-5 text-emerald-300" /> : <Share2 className="w-5 h-5" />}
          </div>
          <div className="flex-1 text-left">
            <div className="font-semibold text-[15px]">
              {p.shared_with_team ? "Partagé avec l'équipe" : "Partager avec l'équipe"}
            </div>
            <div className={`text-xs ${p.shared_with_team ? "text-emerald-200/80" : "text-zinc-500"}`}>
              {p.shared_with_team ? "Tapez pour retirer du partage" : "Vos collègues pourront le voir et le contacter"}
            </div>
          </div>
        </button>
      </div>

      {/* Enrichment card — uses free public FR APIs */}
      <EnrichmentCard prospect={p} onApplied={(updated) => setData({ ...data, lead: updated })} />

      {/* RDV block */}
      {showRdv && (
        <div className="px-5 mt-5" data-testid="rdv-block">
          <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-2 px-1 flex items-center gap-1.5">
            <Calendar className="w-3 h-3" /> Rendez-vous
          </div>
          <div className="bg-emerald-500/10 rounded-3xl p-4 border border-emerald-500/30">
            {rdvFormatted && (
              <div className="mb-2.5 font-display font-bold text-emerald-300 capitalize text-sm">{rdvFormatted}</div>
            )}
            <div className="flex gap-2">
              <Input
                type="datetime-local"
                value={rdvLocal}
                onChange={(e) => setRdvLocal(e.target.value)}
                onBlur={() => { if (rdvLocal !== isoToLocal(p.rdv_at)) saveRdvAt(rdvLocal); }}
                className="rounded-2xl flex-1 bg-[var(--surface)] border-0 shadow-soft h-10 text-sm"
                data-testid="rdv-datepicker"
                disabled={savingRdv}
              />
              {rdvLocal && (
                <Button type="button" variant="ghost" size="sm" onClick={() => { setRdvLocal(""); saveRdvAt(""); }} className="rounded-2xl text-zinc-500 h-10" data-testid="clear-rdv">Effacer</Button>
              )}
            </div>
            {p.rdv_at && (
              <Button onClick={addToCalendar} variant="outline" className="w-full mt-3 rounded-full border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/15 h-10 text-xs font-semibold" data-testid="add-to-calendar">
                <CalendarPlus className="w-3.5 h-3.5 mr-1.5" />
                Ajouter à mon calendrier
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Infos */}
      <div className="px-5 mt-5">
        <GroupedList title="Coordonnées">
          {p.phone && <GroupedListRow icon={Phone} iconColor="emerald" label={p.phone} sublabel="Téléphone" href={`tel:${p.phone}`} as="a" right={<span />} testId="row-phone" />}
          {p.email && <GroupedListRow icon={Mail} iconColor="sky" label={p.email} sublabel="Email" href={`mailto:${p.email}`} as="a" right={<span />} testId="row-email" />}
          {p.address && <GroupedListRow icon={MapPin} iconColor="violet" label={p.address} sublabel="Adresse" right={<span />} testId="row-address" />}
          {p.company && <GroupedListRow icon={Building2} iconColor="amber" label={p.company} sublabel="Entreprise" right={<span />} testId="row-company" />}
          {p.title && <GroupedListRow icon={Briefcase} iconColor="fuchsia" label={p.title} sublabel="Fonction" right={<span />} testId="row-title" />}
          {p.siret && <GroupedListRow icon={Hash} iconColor="slate" label={p.siret} sublabel="SIRET" right={<span />} testId="row-siret" />}
          {!p.phone && !p.email && !p.address && !p.title && !p.siret && (
            <div className="px-4 py-5 text-sm text-zinc-500 italic">Aucune coordonnée renseignée</div>
          )}
        </GroupedList>
      </div>

      {/* Notes */}
      {p.notes && (
        <div className="px-5 mt-5">
          <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-2 px-1">Notes</div>
          <div className="bg-[var(--surface)] rounded-xl p-4 shadow-soft">
            <div className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{p.notes}</div>
          </div>
        </div>
      )}

      {/* Historique */}
      <div className="px-5 mt-5">
        <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-2 px-1">
          Historique · {data.interactions.length}
        </div>
        {data.interactions.length === 0 ? (
          <div className="bg-[var(--surface)] rounded-xl p-8 shadow-soft text-center">
            <div className="w-12 h-12 rounded-2xl bg-[var(--surface-alt)] grid place-items-center mx-auto">
              <MoreHorizontal className="w-5 h-5 text-zinc-500" />
            </div>
            <div className="text-sm text-zinc-500 mt-3">Aucune interaction pour le moment</div>
          </div>
        ) : (
          <div className="bg-[var(--surface)] rounded-xl shadow-soft overflow-hidden divide-y divide-[var(--border-soft)]">
            {data.interactions.map((i) => {
              const typeIcon = { visit: Footprints, call: Phone, email: Mail, linkedin: MessageCircle, whatsapp: MessageCircle }[i.type] || MoreHorizontal;
              const typeColor = { visit: "violet", call: "emerald", email: "sky", linkedin: "fuchsia", whatsapp: "fuchsia" }[i.type] || "slate";
              return (
                <GroupedListRow
                  key={i.interaction_id}
                  icon={typeIcon}
                  iconColor={typeColor}
                  label={{ visit: "Visite terrain", call: "Appel", email: "Email", linkedin: "LinkedIn", whatsapp: "WhatsApp" }[i.type] || i.type}
                  sublabel={`${new Date(i.date).toLocaleDateString("fr-FR")} · ${i.notes || "—"}`}
                  right={<span />}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Delete — danger zone */}
      <div className="px-5 mt-8 pb-4">
        <button
          onClick={del}
          data-testid="delete-prospect"
          className="w-full flex items-center justify-center gap-2 h-12 rounded-2xl text-rose-300 font-semibold active:bg-rose-500/10 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          Supprimer ce lead
        </button>
      </div>
    </AppShell>
  );
}
